// src/modulos/Parametros/ProjectModelPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const PROJECT_MODEL_COLLECTION = 'parameters/data/modelo-de-projeto';
const PROJECTS_COLLECTION = 'projects';
const ITEMS_PER_PAGE = 10;

const ProjectModelPage = () => {
    const can = usePagePermissions('modelos_de_projeto');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [referencingProjects, setReferencingProjects] = useState([]);

    // Hooks de Dados
    const {
        data: projectModels,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(PROJECT_MODEL_COLLECTION, 'name');

    const { data: allProjects } = useFirestore(PROJECTS_COLLECTION);

    // ============================================================
    // 1. DEFINIR filteredModels ANTES dos callbacks que o usam
    // ============================================================
    const filteredModels = useMemo(() => {
        if (!projectModels) return [];
        if (!searchTerm) return projectModels;

        const lowerCaseSearch = searchTerm.toLowerCase();
        return projectModels.filter(item =>
            item.name && item.name.toLowerCase().includes(lowerCaseSearch)
        );
    }, [projectModels, searchTerm]);

    // Ordenar modelos
    const sortedModels = useMemo(() => {
        return [...filteredModels].sort((a, b) => {
            if (!a.updatedAt && !b.updatedAt) return 0;
            if (!a.updatedAt) return 1;
            if (!b.updatedAt) return -1;
            return new Date(b.updatedAt) - new Date(a.updatedAt);
        });
    }, [filteredModels]);

    const totalFilteredItems = sortedModels.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentModels = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedModels.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [sortedModels, currentPage]);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setIsModalOpen(false);
        setNameInput('');
        setFormError('');
        setFormSuccess('');
        setReferencingProjects([]);
    }, []);

    const handleCloseModal = useCallback(() => {
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setIsModalOpen(true);
        setNameInput(item.name);
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
        setReferencingProjects([]);
    }, []);

    // Verificar referências
    const checkProjectReferences = useCallback((modelId) => {
        if (!allProjects || allProjects.length === 0) return [];

        const referencing = allProjects.filter(project =>
            project.projectModelId === modelId ||
            project.modeloProjetoId === modelId ||
            project.modelId === modelId ||
            project.projectModel === modelId
        );

        return referencing;
    }, [allProjects]);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            // Verificar referências
            const references = checkProjectReferences(id);

            if (references && references.length > 0) {
                setReferencingProjects(references);
                setFormError(
                    `❌ Não é possível deletar o modelo "${name}" pois ele está sendo utilizado em ${references.length} projeto(s).`
                );
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setDeleteLoading(false);
                setTimeout(() => {
                    setFormError('');
                    setReferencingProjects([]);
                }, 5000);
                return;
            }

            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`✅ Modelo "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const remainingItems = sortedModels.length - 1;
                if (currentPage > 1 && remainingItems <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("❌ Erro ao deletar Modelo de Projeto: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar modelo. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, checkProjectReferences, deleteDocument, sortedModels, currentPage]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim()) {
            setFormError("O nome do Modelo de Projeto é obrigatório.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: nameInput.trim(),
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Modelo editado com sucesso!' : 'Modelo adicionado com sucesso!');
                setTimeout(() => {
                    resetForm();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, editingItem, addDocument, updateDocument, resetForm]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Novo Modelo de Projeto'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar Modelo'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome do Modelo <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        placeholder="Ex: Padrão, Rápido, Complexo, Ágil"
                        required
                        className={styles.input}
                        disabled={formLoading}
                        autoFocus
                    />
                    <small className={styles.fieldHint}>
                        {editingItem
                            ? 'Altere o nome do modelo de projeto.'
                            : 'Digite um nome único e descritivo para o modelo.'}
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Deleção"
                message={`Tem certeza que deseja deletar o modelo "${deleteConfirm.name}"?\n\nEsta ação é irreversível mas não afetará projetos existentes.`}
                isLoading={deleteLoading}
                danger={true}
            >
                {referencingProjects.length > 0 && (
                    <div className={`${styles.alert} ${styles.warning}`} style={{ margin: '16px 0' }}>
                        <span className={styles.alertIcon}>⚠️</span>
                        <strong>Projetos relacionados ({referencingProjects.length}):</strong>
                        <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                            {referencingProjects.slice(0, 5).map(project => (
                                <li key={project.id}>• {project.name || project.title || 'Projeto sem nome'}</li>
                            ))}
                            {referencingProjects.length > 5 && (
                                <li>... e mais {referencingProjects.length - 5} projeto(s)</li>
                            )}
                        </ul>
                    </div>
                )}
            </ConfirmModal>

            {/* Page Header */}
            <PageHeader
                title="Modelo de Projeto"
                actionText="Adicionar Modelo"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome do modelo de projeto..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={2} rows={ITEMS_PER_PAGE} />
                    ) : currentModels.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📋'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhum modelo cadastrado'
                            }
                            message={
                                searchTerm
                                    ? `Nenhum modelo encontrado para "${searchTerm}"`
                                    : "Clique em 'Adicionar Modelo' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Modelo de Projeto</th>
                                            <th style={{ width: '200px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentModels.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Última atualização: {new Date(item.updatedAt).toLocaleString('pt-BR')}
                                                        </small>
                                                    )}
                                                    {item.createdAt && !item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Criado em: {new Date(item.createdAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                            title="Editar modelo"
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                            title="Deletar modelo"
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="modelos"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ProjectModelPage;